import { Component, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TotemWidget } from '../totem-widget';

type DaySurveyDraft = {
    rating: number;
    comment: string;
    submitted: boolean;
};

@Component({
    selector: 'app-totem-widget-day-survey',
    templateUrl: './totem-widget-day-survey.component.html',
    styleUrls: ['./totem-widget-day-survey.component.scss'],
    standalone: false
})
export class TotemWidgetDaySurveyComponent extends TotemWidget implements OnChanges {
    size: 'md' = 'md';

    /** Stable key for the selected tour day (e.g. day index). */
    @Input() dayKey: string = null;

    rating = 3;
    comment = '';
    submitted = false;

    readonly stars = [1, 2, 3, 4, 5];

    /** In-memory only: answers for the current session, keyed by day. */
    private _drafts: { [dayKey: string]: DaySurveyDraft } = {};
    private _activeKey: string = null;

    constructor(public elementRef: ElementRef) {
        super(elementRef);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['dayKey']) {
            this.applyDayKey(this.dayKey);
        }
    }

    /**
     * Switch visible draft to the given day.
     * Saves the previous day's rating/comment before loading the next.
     */
    applyDayKey(dayKey: string) {
        if (this._activeKey != null) {
            this._drafts[this._activeKey] = {
                rating: this.rating,
                comment: this.comment,
                submitted: this.submitted,
            };
        }

        this.dayKey = dayKey;
        this._activeKey = dayKey != null && dayKey !== '' ? String(dayKey) : '_default';

        const draft = this._drafts[this._activeKey];
        this.rating = draft?.rating ?? 3;
        this.comment = draft?.comment ?? '';
        this.submitted = !!draft?.submitted;
    }

    setRating(value: number) {
        if (this.submitted) return;
        this.rating = value;
        this._saveDraft(false);
    }

    onCommentChange() {
        if (this.submitted) return;
        this._saveDraft(false);
    }

    canSubmit() {
        return !this.submitted && this.rating > 0;
    }

    submit() {
        if (!this.canSubmit()) return;
        this._saveDraft(true);
        this.submitted = true;
    }

    private _saveDraft(submitted: boolean) {
        const key = this._activeKey || (this.dayKey != null && this.dayKey !== '' ? String(this.dayKey) : '_default');
        this._activeKey = key;
        this._drafts[key] = {
            rating: this.rating,
            comment: this.comment,
            submitted,
        };
    }
}
